import React, { useState } from 'react';
import { FormValidator } from 'forms';
import { ContactSection } from './ContactSection';
import { DetailSection } from './DetailSection';
import { FileUploadSection } from './FileUploadSection';
import { SubmitSection } from './SubmitSection';
import { Schema, FormData } from './types';
import { useFormFields } from './hooks/useFormFields';
import { usePPTTranscription } from './hooks/usePPTTranscription';
import { useFileUpload } from './hooks/useFileUpload';
import { getDisplayValue, getInputClass } from './helpers';

interface QualificationFormProps {
  schema: Schema;
  onSubmit: (data: FormData) => void;
  isSubmitting?: boolean;
  category: string;
  userRoleType?: string;
  userPillar?: string;
  initialValues?: FormData | null;
  isEditMode?: boolean;
}

function QualificationForm({
  schema,
  onSubmit,
  isSubmitting = false,
  category,
  userRoleType,
  userPillar,
  initialValues = null,
  isEditMode = false,
}: QualificationFormProps) {
  const {
    formData,
    errors,
    setErrors,
    handleChange,
    handleArrayChange,
    addArrayItem,
    removeArrayItem,
  } = useFormFields(schema, initialValues);

  const { pptTranscription, startPPTTranscription } =
    usePPTTranscription(category);

  const {
    files,
    handleFileSelected,
    handleFileUpload,
    handleFileUploadStart,
    handleFileUploadError,
  } = useFileUpload(startPPTTranscription, initialValues);

  const isFinalStage = initialValues?.competition_stage === 'final';

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitClicked(true); // Immediately change button state
    // console.log('📋 QualificationForm: Form submission started');
    // console.log('📁 Current file state at submission:', files);

    // Check if files are still uploading
    const isUploading =
      files.presentation.uploading || (isFinalStage && files.video.uploading);
    if (isUploading) {
      console.warn('⏳ Cannot submit: Files are still uploading');
      setErrors({
        files: [
          'Please wait for all files to finish uploading before submitting.',
        ],
      });
      setIsSubmitClicked(false); // Reset button state
      return;
    }

    // Check if required files are uploaded
    if (!files.presentation.url) {
      console.error('❌ Missing presentation file');
      setErrors({ files: ['Presentation file is required.'] });
      setIsSubmitClicked(false); // Reset button state
      return;
    }

    if (isFinalStage && !files.video.url) {
      console.error('❌ Missing video file');
      setErrors({ files: ['Video file is required.'] });
      setIsSubmitClicked(false); // Reset button state
      return;
    }

    const validator = new FormValidator();
    const result = validator.validateFormData(formData, schema);

    // console.log('✅ Form validation result:', result);

    if (result.isValid) {
      setErrors({});

      // Prepare base submission data
      const enhancedFormData = { ...result.validatedData };

      // Use pre-processed PPT data if available
      if (pptTranscription.isCompleted && pptTranscription.extractedData) {
        // console.log('✅ Using pre-processed PPT data');
        enhancedFormData.additional_comments = pptTranscription.extractedData;
        // console.log('📊 Using extraction length:', pptTranscription.extractedData.length);
      } else if (pptTranscription.error) {
        console.warn(
          '⚠️ PPT processing had errors, using default additional_comments:',
          pptTranscription.error
        );
        // Keep the original additional_comments value (likely "default")
      } else {
        console.warn(
          '⚠️ PPT processing not completed, this should not happen if submit button logic is correct'
        );
        // Keep the original additional_comments value (likely "default")
      }

      // Include file URLs and competition stage in the submission data
      const dataWithFiles = {
        ...enhancedFormData,
        award_material: files.presentation.url,
        video_url: files.video.url,
        competition_stage: initialValues?.competition_stage || 'qualification',
      };

      // console.log('🚀 Submitting form with file URLs:', {
      //   award_material: dataWithFiles.award_material,
      //   video_url: dataWithFiles.video_url,
      //   competition_stage: dataWithFiles.competition_stage,
      //   additional_comments_length: dataWithFiles.additional_comments?.length || 0,
      //   totalFields: Object.keys(dataWithFiles).length
      // });

      // console.log('📤 Complete submission data:', dataWithFiles);

      onSubmit(dataWithFiles);
    } else {
      console.error('❌ Form validation failed:', result.errors);
      setErrors(result.errors);
      setIsSubmitClicked(false); // Reset button state
    }
  };

  // Split fields into logical groups
  const contactFields = schema.fields.slice(0, 6);
  const detailFields = schema.fields.slice(6);

  // Check if form can be submitted
  const isFilesUploading =
    files.presentation.uploading || (isFinalStage && files.video.uploading);
  const isFilesMissing =
    !files.presentation.url || (isFinalStage && !files.video.url);
  // isPPTProcessing should only block submission if the user selected a NEW file.
  // If we are in edit mode with a pre-filled URL (isNewFile === false), we must
  // not wait for transcription that was never started.
  const isPPTProcessing =
    files.presentation.isNewFile &&
    (pptTranscription.isTranscribing ||
      (!pptTranscription.isCompleted && !pptTranscription.error));
  const canSubmit =
    !isSubmitting && !isFilesUploading && !isFilesMissing && !isPPTProcessing;

  const sharedFieldProps = {
    formData,
    errors,
    getDisplayValue: (field: Parameters<typeof getDisplayValue>[0]) =>
      getDisplayValue(field, formData),
    getInputClass: (fieldName: string, baseClass?: string) =>
      getInputClass(fieldName, errors, baseClass),
    handleChange,
    handleArrayChange,
    addArrayItem,
    removeArrayItem,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Contact Information */}
      <ContactSection fields={contactFields} {...sharedFieldProps} />

      {/* Detailed Information */}
      <DetailSection fields={detailFields} {...sharedFieldProps} />

      {/* File Upload Section - Only for Qualification */}
      <FileUploadSection
        files={files}
        pptTranscription={pptTranscription}
        errors={errors}
        handleFileSelected={handleFileSelected}
        handleFileUpload={handleFileUpload}
        handleFileUploadStart={handleFileUploadStart}
        handleFileUploadError={handleFileUploadError}
        isFinalStage={isFinalStage}
      />

      {/* Submit Section */}
      <SubmitSection
        canSubmit={canSubmit}
        isSubmitClicked={isSubmitClicked}
        isSubmitting={isSubmitting}
        isFilesUploading={isFilesUploading}
        isPPTProcessing={isPPTProcessing}
        isFilesMissing={isFilesMissing}
      />
    </form>
  );
}

export default QualificationForm;
